import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface SplashScreenProps {
  onComplete: () => void;
}
export function SplashScreen({
  onComplete
}: SplashScreenProps) {
  const [count] = useState(0);
  const text = 'ORIAM';
  const characters = text.split('');
  useEffect(() => {
    // Complete splash screen after animation finishes
    const timer = setTimeout(() => {
      onComplete();
    }, 3000); // 3 seconds total (animation + small delay)
    return () => clearTimeout(timer);
  }, [onComplete]);
  return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[var(--bg)] overflow-hidden" data-id="element-204">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" data-id="element-205">
        <div className="absolute inset-0 opacity-[0.35]" style={{
        backgroundSize: '50px 50px',
        backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px)',
        maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 0%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 0%, transparent 100%)'
      }} data-id="element-206"></div>
        <div className="absolute w-[600px] h-[600px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full blur-[100px] opacity-40 bg-[radial-gradient(circle,rgba(0,87,255,0.6)_0%,transparent_70%)]" data-id="element-207"></div>
        <div className="absolute w-[400px] h-[400px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full blur-[80px] opacity-30 bg-[radial-gradient(circle,rgba(204,255,0,0.4)_0%,transparent_70%)]" data-id="element-208"></div>
      </div>

      {/* Main Text Container */}
      <div className="relative z-10 w-full px-4 flex flex-col items-center" data-id="element-209">
        <AnimatePresence mode="wait" data-id="element-210">
          <motion.div key={count} className="flex flex-wrap justify-center items-center w-full" data-id="element-211">
            {characters.map((char, i) => <div key={i} className="relative px-[0.5vw] overflow-hidden" data-id="element-212">
                {/* Main Character */}
                <motion.span initial={{
              opacity: 0,
              filter: 'blur(10px)'
            }} animate={{
              opacity: 1,
              filter: 'blur(0px)'
            }} transition={{
              delay: i * 0.08 + 0.3,
              duration: 0.8
            }} className="text-[20vw] md:text-[15vw] leading-none font-black text-[var(--text)] tracking-tighter" data-id="element-213">
                  {char}
                </motion.span>

                {/* Top Slice Layer */}
                <motion.span initial={{
              x: '-100%',
              opacity: 0
            }} animate={{
              x: '100%',
              opacity: [0, 1, 0]
            }} transition={{
              duration: 0.7,
              delay: i * 0.08,
              ease: 'easeInOut'
            }} className="absolute inset-0 text-[20vw] md:text-[15vw] leading-none font-black text-[var(--neon)] z-10 pointer-events-none" style={{
              clipPath: 'polygon(0 0, 100% 0, 100% 35%, 0 35%)'
            }} data-id="element-214">
                  {char}
                </motion.span>

                {/* Middle Slice Layer */}
                <motion.span initial={{
              x: '100%',
              opacity: 0
            }} animate={{
              x: '-100%',
              opacity: [0, 1, 0]
            }} transition={{
              duration: 0.7,
              delay: i * 0.08 + 0.1,
              ease: 'easeInOut'
            }} className="absolute inset-0 text-[20vw] md:text-[15vw] leading-none font-black text-[var(--blue)] z-10 pointer-events-none" style={{
              clipPath: 'polygon(0 35%, 100% 35%, 100% 65%, 0 65%)'
            }} data-id="element-215">
                  {char}
                </motion.span>

                {/* Bottom Slice Layer */}
                <motion.span initial={{
              x: '-100%',
              opacity: 0
            }} animate={{
              x: '100%',
              opacity: [0, 1, 0]
            }} transition={{
              duration: 0.7,
              delay: i * 0.08 + 0.2,
              ease: 'easeInOut'
            }} className="absolute inset-0 text-[20vw] md:text-[15vw] leading-none font-black text-[var(--neon)] z-10 pointer-events-none" style={{
              clipPath: 'polygon(0 65%, 100% 65%, 100% 100%, 0 100%)'
            }} data-id="element-216">
                  {char}
                </motion.span>
              </div>)}
          </motion.div>
        </AnimatePresence>

        {/* Subtitle fade in */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.5,
        duration: 0.8
      }} className="text-[var(--text-muted)] text-sm md:text-base tracking-[0.3em] uppercase font-bold mt-4" data-id="element-217">
          Marketing
        </motion.p>
      </div>

      {/* Loading indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2,
      duration: 0.5
    }} className="absolute bottom-12 left-1/2 -translate-x-1/2" data-id="element-218">
        <div className="flex gap-2" data-id="element-219">
          <motion.div animate={{
          scale: [1, 1.2, 1]
        }} transition={{
          repeat: Infinity,
          duration: 0.8,
          delay: 0
        }} className="w-2 h-2 rounded-full bg-[var(--neon)]" data-id="element-220" />
          <motion.div animate={{
          scale: [1, 1.2, 1]
        }} transition={{
          repeat: Infinity,
          duration: 0.8,
          delay: 0.2
        }} className="w-2 h-2 rounded-full bg-[var(--blue)]" data-id="element-221" />
          <motion.div animate={{
          scale: [1, 1.2, 1]
        }} transition={{
          repeat: Infinity,
          duration: 0.8,
          delay: 0.4
        }} className="w-2 h-2 rounded-full bg-[var(--neon)]" data-id="element-222" />
        </div>
      </motion.div>
    </div>;
}